/* Delivery Lifecycle — where the window's shipments are now, and whether the scan
   feed that tells us is actually working.

   Read the coverage banner first. order_status is advanced by PCIS polling and
   inbound courier webhooks, and both only run for native accounts; Clickpost-routed
   orders are tracked by Clickpost, so their status here is frozen at whatever PCIS
   last saw. At 60% native coverage this tab describes 60% of the business, and it
   says so rather than quietly averaging the two populations together. */
import React from 'react'
import PropTypes from 'prop-types'
import {Box, Stack, Tooltip, Typography} from '@mui/material'
import {LoadErrorPanel, TableSkeleton} from '../../components'
import {usePcisHealthEventsQuery, usePcisHealthLifecycleQuery} from '../../../../redux/supplyApiSlice'
import {AMBER, CORAL, Empty, INDIGO, Meter, MiniTable, Panel, Pill, SLATE, Stat, StatGrid, TEAL, VIOLET, nf, pct} from './parts'

/* Stage → colour. Keys match the server's stage names exactly; an unlisted stage
   falls back to slate rather than throwing off the palette. */
const STAGE_PALETTE = {
  'Awaiting pickup': INDIGO,
  'Pickup failed': CORAL,
  'Picked up': INDIGO,
  'In transit': VIOLET,
  'Out for delivery': VIOLET,
  Delivered: TEAL,
  'Undelivered / NDR': AMBER,
  RTO: CORAL,
  Cancelled: SLATE,
  'Lost / damaged': CORAL,
  'Other / unknown': SLATE,
}
const paletteFor = (stage) => STAGE_PALETTE[stage] || SLATE

/* A drop bigger than this between funnel stages gets called out in red. */
const NOTABLE_DROP_PCT = 10

export default function TabLifecycle({filters, pollMs}) {
  const poll = pollMs ? {pollingInterval: pollMs} : undefined

  const {data: life, isFetching, isError, error, refetch} = usePcisHealthLifecycleQuery(filters, poll)
  const {data: events} = usePcisHealthEventsQuery({...filters, unmappedLimit: 12}, poll)

  if (isError) return <LoadErrorPanel message={error?.message || 'Could not load lifecycle data.'} onRetry={refetch} />
  if (isFetching && !life) return <TableSkeleton kpis={4} rows={6} />
  if (!life || !life.totalOrders) return <Empty />

  const coverage = life.nativeCoveragePct ?? 0
  const funnelTop = life.funnel?.[0]?.count || 1
  const visibleStages = (life.stages || []).filter((s) => s.count > 0)

  return (
    <>
      {/* Coverage caveat, stated before any number is read. */}
      <Box
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: '12px',
          borderStyle: 'dashed',
          bgcolor: `${coverage >= 90 ? TEAL.main : AMBER.main}0F`,
          p: 1.75,
          mb: 2.5,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{minWidth: 200, flex: 1}}>
          <Typography sx={{fontSize: 12.5, fontWeight: 700, mb: 0.5}}>
            Status coverage: {pct(coverage)} of this window is PCIS-native
          </Typography>
          <Meter value={coverage} palette={coverage >= 90 ? TEAL : AMBER} height={7} />
        </Box>
        <Typography sx={{fontSize: 12, color: 'text.secondary', flex: 2, minWidth: 260, lineHeight: 1.55}}>
          Tracking status is only maintained for native orders — Clickpost-routed shipments are tracked by Clickpost, so their
          status here stays at whatever PCIS last recorded. Add a <b>Routing = PCIS native</b> filter to read this tab without
          that dilution.
        </Typography>
      </Box>

      <Panel title='Forward flow' note='How far shipments got. Each bar is cumulative — an RTO shipment still counts as picked up and attempted.'>
        <Stack spacing={1.25}>
          {(life.funnel || []).map((s, i) => {
            const prev = life.funnel[i - 1]
            const drop = prev && prev.count ? ((prev.count - s.count) / prev.count) * 100 : 0
            return (
              <Stack key={s.stage} direction='row' alignItems='center' spacing={1.5}>
                <Typography sx={{width: 118, flexShrink: 0, fontSize: 12.5, fontWeight: 600}}>{s.stage}</Typography>
                <Box sx={{flex: 1, position: 'relative', height: 30, bgcolor: 'action.hover', borderRadius: '7px', overflow: 'hidden', minWidth: 120}}>
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: '0 auto 0 0',
                      width: `${(s.count / funnelTop) * 100}%`,
                      backgroundImage: `linear-gradient(90deg, ${paletteFor(s.stage).g1}, ${paletteFor(s.stage).g2})`,
                      borderRadius: '7px',
                      transition: 'width .35s ease',
                    }}
                  />
                  <Typography
                    sx={{position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', fontSize: 11.5, fontWeight: 700, color: '#fff', fontVariantNumeric: 'tabular-nums'}}
                  >
                    {nf(s.count)}
                  </Typography>
                </Box>
                <Typography sx={{width: 52, textAlign: 'right', fontSize: 11.5, color: 'text.secondary', fontVariantNumeric: 'tabular-nums'}}>
                  {pct(s.sharePct)}
                </Typography>
                <Typography
                  sx={{width: 62, textAlign: 'right', fontSize: 11, fontWeight: 600, color: drop >= NOTABLE_DROP_PCT ? CORAL.main : 'text.disabled', fontVariantNumeric: 'tabular-nums'}}
                >
                  {i === 0 ? '' : `−${drop.toFixed(1)}%`}
                </Typography>
              </Stack>
            )
          })}
        </Stack>
      </Panel>

      <StatGrid sx={{mt: 2}} min={170}>
        {visibleStages.map((s) => (
          <Stat
            key={s.stage}
            label={s.stage}
            value={Number(s.sharePct).toFixed(1)}
            unit='%'
            palette={paletteFor(s.stage)}
            share={s.sharePct}
            sub={`${nf(s.count)} shipments`}
          />
        ))}
      </StatGrid>

      <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: '1fr 1fr'}, gap: 2, mt: 2}}>
        <Panel
          title='Status feed health'
          note='Scans captured vs actually forwarded to ProShip. Scoped by event time, so page filters do not narrow this.'
        >
          <MiniTable
            columns={[
              {key: 'source', header: 'Source', render: (r) => <Typography sx={{fontSize: 12.5, fontWeight: 700}}>{r.source}</Typography>},
              {key: 'total', header: 'Events', align: 'right', render: (r) => nf(r.total)},
              {key: 'forwarded', header: 'Forwarded', align: 'right', render: (r) => nf(r.forwarded)},
              {
                key: 'stuck',
                header: 'Stuck',
                align: 'right',
                render: (r) => (
                  <Typography sx={{fontSize: 12, fontWeight: r.stuck ? 700 : 400, color: r.stuck ? CORAL.main : 'text.disabled'}}>
                    {nf(r.stuck)}
                  </Typography>
                ),
              },
              {
                key: 'health',
                header: 'Health',
                render: (r) => (
                  <Pill palette={r.stuckRatePct >= 5 ? CORAL : r.stuckRatePct >= 1 ? AMBER : TEAL} title={`${pct(r.stuckRatePct, 2)} of events never forwarded`}>
                    {r.stuckRatePct >= 5 ? 'Failing' : r.stuckRatePct >= 1 ? 'Degraded' : 'Healthy'}
                  </Pill>
                ),
              },
            ]}
            rows={events?.sources || []}
            getKey={(r) => r.source}
            empty='No status events in this window.'
          />
          <Typography sx={{fontSize: 11.5, color: 'text.secondary', mt: 1.75, lineHeight: 1.6}}>
            “Stuck” counts <Box component='span' sx={{fontFamily: 'monospace'}}>order_events</Box> rows still at{' '}
            <Box component='span' sx={{fontFamily: 'monospace'}}>RECEIVED</Box> — PCIS captured the scan but never relayed it, so
            the customer never saw it.
          </Typography>
        </Panel>

        <Panel title='Unmapped courier statuses' note='Scans that arrived with no mapping and were recorded as code 0'>
          <MiniTable
            columns={[
              {key: 'courierCode', header: 'Courier', render: (r) => <Typography sx={{fontSize: 12.5, fontWeight: 700}}>{r.courierCode}</Typography>},
              {
                key: 'courierStatus',
                header: 'Raw status',
                render: (r) => (
                  <Tooltip title={r.courierStatus || ''}>
                    <Typography sx={{fontSize: 11.5, fontFamily: 'monospace', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                      {r.courierStatus || '—'}
                    </Typography>
                  </Tooltip>
                ),
              },
              {key: 'count', header: 'Count', align: 'right', render: (r) => <Typography sx={{fontSize: 12, fontWeight: 700}}>{nf(r.count)}</Typography>},
            ]}
            rows={events?.unmapped || []}
            getKey={(r) => `${r.courierCode}|${r.courierStatus}`}
            empty='Every courier status in this window mapped cleanly.'
          />
          {events?.unmapped?.length ? (
            <Typography sx={{fontSize: 11.5, color: 'text.secondary', mt: 1.75, lineHeight: 1.6}}>
              Each of these is a scan ProShip received as “unknown”. Adding the mapping on the Status Mapping admin screen fixes
              it for every future scan.
            </Typography>
          ) : null}
        </Panel>
      </Box>
    </>
  )
}

TabLifecycle.propTypes = {filters: PropTypes.object.isRequired, pollMs: PropTypes.number}
